import { getWorkflowLabel } from '@/utils/badgeUtils'

// How long the "Updated from Excel" label stays visible after an upload
export const EXCEL_UPDATE_VISIBLE_DAYS = 7

export interface ExcelChange {
  field: string
  label: string
  from?: string | number | null
  to?: string | number | null
}

export interface ExcelUpdateInfo {
  updatedAt: string
  changes: ExcelChange[]
  restoredFrom?: string
  restoredTo?: string
}

const DATE_FIELDS = ['deadline']
const MONEY_FIELDS = ['estimatedCost', 'emd', 'documentFees']

// Parses tender.excelUpdateInfo; returns null when missing, invalid or older than 7 days
export const getExcelUpdate = (
  tender?: { excelUpdateInfo?: string | null } | null,
): ExcelUpdateInfo | null => {
  if (!tender?.excelUpdateInfo) return null
  try {
    const info = JSON.parse(tender.excelUpdateInfo) as ExcelUpdateInfo
    const updatedAt = new Date(info.updatedAt)
    if (isNaN(updatedAt.getTime())) return null

    const ageDays = (Date.now() - updatedAt.getTime()) / (1000 * 60 * 60 * 24)
    if (ageDays > EXCEL_UPDATE_VISIBLE_DAYS) return null

    if (!info.changes?.length && !info.restoredTo) return null
    return { ...info, changes: info.changes || [] }
  } catch {
    return null
  }
}

const formatValue = (field: string, value?: string | number | null): string => {
  if (value === null || value === undefined || value === '') return '—'
  if (DATE_FIELDS.includes(field)) {
    const date = new Date(value)
    if (isNaN(date.getTime())) return String(value)
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
  }
  if (MONEY_FIELDS.includes(field)) {
    return `₹${Number(value).toLocaleString('en-IN')}`
  }
  return String(value)
}

// "Deadline: 20 Oct 2026 → 30 Oct 2026", or "Tender brief changed" for long text
export const formatExcelChange = (change: ExcelChange): string => {
  if (change.from === undefined && change.to === undefined) return `${change.label} changed`
  return `${change.label}: ${formatValue(change.field, change.from)} → ${formatValue(change.field, change.to)}`
}

export const formatExcelRestore = (info: ExcelUpdateInfo): string | null => {
  if (!info.restoredTo) return null
  return `Brought back from Not Bidded to ${getWorkflowLabel(info.restoredTo)}`
}

export const formatExcelUpdateDate = (info: ExcelUpdateInfo): string =>
  new Date(info.updatedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })

// Plain-text summary used as the hover text on the list tag
export const getExcelUpdateSummary = (info: ExcelUpdateInfo): string => {
  const lines = [
    `Updated from Excel on ${formatExcelUpdateDate(info)}`,
    formatExcelRestore(info),
    ...info.changes.map(formatExcelChange),
  ].filter(Boolean)
  return lines.join('\n')
}
